import React, { useState, useEffect, useRef } from "react";
import { Toaster, toast } from "sonner";
import "./SectionStyles.css";
import {
  getFileStructure,
  createFolder,
  uploadFile,
  deleteItem,
  renameItem,
  moveItem,
  getFolderContents,
  getFileInfo,
  searchItems,
} from "../../services/myLibraryService";
import ConfirmDeleteModal from "../common/ConfirmDeleteModal";
import InputModal from "../common/InputModal";
import Breadcrumbs from "../common/Breadcrumbs";
import FileGridItem from "../common/FileGridItem";
import LoadingSpinner from "../common/LoadingSpinner";
import EmptyState from "../common/EmptyState";
import {
  getFileTypeIcon,
  formatFileSize,
  SUPPORTED_VIDEO_FORMATS,
  SUPPORTED_AUDIO_FORMATS,
  isValidMediaFile,
  getMediaFileDetails,
} from "../common/fileUtils";

// Helper to build folder tree (only folders, no files)
function buildFolderTree(items, parentId = null) {
  return items
    .filter((item) => item.type === "folder" && item.parentId === parentId)
    .map((item) => ({
      ...item,
      children: buildFolderTree(items, item._id),
    }));
}

// Helper to expand all parent folders in the path
const getExpandedFolders = (path) => {
  const expanded = {};
  path.forEach((folder) => {
    expanded[folder._id] = true;
  });
  return expanded;
};

// Helper to get all items in a folder (including nested)
function getAllItemsInFolder(items, folderId) {
  const folder = items.find((item) => item._id === folderId);
  if (!folder) return [];

  const directItems = items.filter((item) => item.parentId === folderId);
  const nestedItems = directItems
    .filter((item) => item.type === "folder")
    .flatMap((item) => getAllItemsInFolder(items, item._id));

  return [...directItems, ...nestedItems];
}

// Helper to flatten nested structure
function flattenStructure(items) {
  return items.reduce((acc, item) => {
    acc.push(item);
    if (item.children && item.children.length > 0) {
      acc.push(...flattenStructure(item.children));
    }
    return acc;
  }, []);
}

const styles = {
  container: {
    minHeight: "calc(100vh - 80px)",
    minWidth: "100vw",
    position: "relative",
    marginTop: 0,
    background: "linear-gradient(135deg, #e0e7ef 0%, #c7d2fe 100%)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid #e5e7eb",
    padding: "22px 40px 12px 40px",
    background: "rgba(248,250,252,0.95)",
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    position: "sticky",
    top: 0,
    zIndex: 2,
    gap: 16,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  folderTree: {
    padding: "10px",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    borderRadius: "8px",
    marginBottom: "20px",
  },
  folderRow: {
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
    margin: "2px 0",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)",
    },
  },
  selectedFolder: {
    backgroundColor: "rgba(37, 99, 235, 0.2)",
    color: "#2563eb",
    fontWeight: 600,
  },
  folderIcon: {
    marginRight: "8px",
    fontSize: "1.2em",
  },
  folderToggle: {
    marginRight: "8px",
    fontSize: "0.8em",
    color: "#64748b",
    width: "20px",
    textAlign: "center",
  },
  folderName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  nestedFolder: {
    marginLeft: "20px",
  },
  fileCard: {
    background: "rgba(255,255,255,0.92)",
    borderRadius: 14,
    boxShadow: "0 2px 8px 0 rgba(31,38,135,0.06)",
    padding: 16,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    transition: "box-shadow 0.2s, background 0.2s",
    border: "1.2px solid #e0e7ef",
    minHeight: 160,
    minWidth: 0,
    maxWidth: 140,
    position: "relative",
  },
  fileContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    flex: 1,
  },
  fileActions: {
    display: "flex",
    flexDirection: "row",
    gap: 8,
    width: "100%",
    justifyContent: "center",
    position: "absolute",
    bottom: 16,
    left: 0,
    padding: "0 16px",
  },
  actionButton: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    padding: "6px",
    fontSize: 12,
    fontWeight: 500,
    color: "#2563eb",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
    width: 28,
    height: 28,
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
  },
  deleteButton: {
    color: "#ef4444",
    borderColor: "#fecaca",
    background: "#fff5f5",
  },
  searchContainer: {
    position: "relative",
    width: "100%",
  },
  suggestionsDropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    backgroundColor: "white",
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    zIndex: 1000,
    maxHeight: "300px",
    overflowY: "auto",
  },
  suggestionItem: {
    padding: "8px 16px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  suggestionIcon: {
    width: "20px",
    height: "20px",
    color: "#666",
  },
  suggestionPath: {
    fontSize: "12px",
    color: "#666",
    marginLeft: "4px",
  },
};

const FolderTree = ({ tree, selectedId, onSelect, expanded, onToggle }) => {
  if (!tree || tree.length === 0) return null;

  return (
    <ul style={{ padding: 0, margin: 0, listStyle: "none" }}>
      {tree.map((item) => {
        // Only render folders
        if (item.type !== "folder") return null;

        return (
          <li key={item._id}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "8px 12px",
                margin: "2px 0",
                borderRadius: 10,
                cursor: "pointer",
                transition: "all 0.2s ease",
                background:
                  selectedId === item._id
                    ? "rgba(37,99,235,0.08)"
                    : "transparent",
                color: selectedId === item._id ? "#2563eb" : "#64748b",
                fontWeight: selectedId === item._id ? 600 : 400,
              }}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(item._id);
              }}
            >
              {item.children && item.children.length > 0 && (
                <span
                  style={{
                    marginRight: 8,
                    fontSize: 16,
                    color: selectedId === item._id ? "#2563eb" : "#64748b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle(item._id);
                  }}
                >
                  {expanded[item._id] ? "▼" : "▶"}
                </span>
              )}
              <span style={{ fontSize: 16, marginRight: 8 }}>📁</span>
              <span
                style={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: 14,
                }}
              >
                {item.name}
              </span>
            </div>
            {expanded[item._id] &&
              item.children &&
              item.children.length > 0 && (
                <div style={{ marginLeft: 24 }}>
                  <FolderTree
                    tree={item.children}
                    selectedId={selectedId}
                    onSelect={onSelect}
                    expanded={expanded}
                    onToggle={onToggle}
                  />
                </div>
              )}
          </li>
        );
      })}
    </ul>
  );
};

function getBreadcrumbs(items, currentId) {
  const crumbs = [];
  let curr = items.find((item) => item._id === currentId);
  while (curr) {
    crumbs.unshift(curr);
    curr = curr.parentId
      ? items.find((item) => item._id === curr.parentId)
      : null;
  }
  return crumbs;
}

const MyLibrary = () => {
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [currentFolderContents, setCurrentFolderContents] = useState([]);
  const [currentFolderPath, setCurrentFolderPath] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeoutRef = useRef(null);
  const [expanded, setExpanded] = useState({});

  // Fetch initial file structure (root folders)
  useEffect(() => {
    const fetchStructure = async () => {
      try {
        setLoading(true);
        const data = await getFileStructure();
        // Support new API structure: { success, message, data: [...] }
        const itemsArray = Array.isArray(data)
          ? data
          : data && Array.isArray(data.data)
          ? data.data
          : [];
        setItems(itemsArray);
        setCurrentFolderContents(itemsArray.filter((item) => !item.parentId));
        setCurrentFolderPath([]);
        setError(null);
      } catch (err) {
        setError(err.message);
        toast.error("Failed to load file structure");
      } finally {
        setLoading(false);
      }
    };

    fetchStructure();
  }, []);

  // Update expanded state when folder path changes
  useEffect(() => {
    if (currentFolderPath.length > 0) {
      const newExpanded = getExpandedFolders(currentFolderPath);
      setExpanded(newExpanded);
    }
  }, [currentFolderPath]);

  // Fetch folder contents when a folder is selected
  useEffect(() => {
    const fetchFolderContents = async () => {
      if (!selectedId) {
        setCurrentFolderContents(
          Array.isArray(items) ? items.filter((item) => !item.parentId) : []
        );
        setCurrentFolderPath([]);
        return;
      }

      try {
        setLoading(true);
        const response = await getFolderContents(selectedId);
        setCurrentFolderContents(response.contents);
        setCurrentFolderPath(response.path);
      } catch (err) {
        toast.error("Failed to load folder contents");
        setCurrentFolderContents([]);
        setCurrentFolderPath([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFolderContents();
  }, [selectedId, items]);

  const handleCreateFolder = async () => {
    if (!newFolderName || newFolderName.trim() === "") {
      toast.error("Please enter a folder name");
      return;
    }

    try {
      setLoading(true);
      await createFolder(newFolderName.trim(), selectedId);
      toast.success("Folder created successfully");
      setShowNewFolderModal(false);
      setNewFolderName("");
      // Refresh the folder structure
      const data = await getFileStructure();
      setItems(data);
      // If we're in a folder, refresh its contents
      if (selectedId) {
        const folderData = await getFolderContents(selectedId);
        setCurrentFolderContents(folderData.contents || []);
        setCurrentFolderPath(folderData.path || []);
      }
    } catch (error) {
      console.error("Error creating folder:", error);
      toast.error(error.message || "Failed to create folder");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async () => {
    try {
      setLoading(true);
      await deleteItem(selectedId);

      // If the deleted item is the currently selected folder, go to root
      setSelectedId(null);

      // Refetch the folder structure and update state
      const data = await getFileStructure();
      setItems(data);
      setCurrentFolderContents(data.filter((item) => !item.parentId));
      setCurrentFolderPath([]);

      toast.success("Item deleted successfully");
      setShowDeleteModal(false);
    } catch (error) {
      toast.error(error.message || "Failed to delete item");
    } finally {
      setLoading(false);
    }
  };

  const refreshFileList = async () => {
    try {
      setLoading(true);
      // Get fresh file structure
      const data = await getFileStructure();
      setItems(data);

      // If we're in a specific folder, refresh its contents
      if (selectedId) {
        const folderContents = await getFolderContents(selectedId);
        setCurrentFolderContents(folderContents.contents);
        setCurrentFolderPath(folderContents.path);
      } else {
        // If we're in root, filter for root items
        setCurrentFolderContents(data.filter((item) => !item.parentId));
        setCurrentFolderPath([]);
      }
    } catch (error) {
      console.error("Error refreshing file list:", error);
      toast.error("Failed to refresh file list");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const extension = file.name.split(".").pop().toLowerCase();
    const isVideo = SUPPORTED_VIDEO_FORMATS.includes(extension);
    const isAudio = SUPPORTED_AUDIO_FORMATS.includes(extension);

    // Check if it's a media file
    if (isVideo || isAudio) {
      // Validate media file
      if (!isValidMediaFile(file)) {
        toast.error(
          `Invalid ${
            isVideo ? "video" : "audio"
          } file. Please ensure the file is a supported format and under 500MB.`
        );
        event.target.value = "";
        return;
      }

      // Show upload progress for media files
      toast.info(`Uploading ${isVideo ? "video" : "audio"}: ${file.name}`);
    }

    try {
      setLoading(true);
      const uploadToast = toast.loading(`Uploading ${file.name}...`);

      const newFile = await uploadFile(file, selectedId);

      if (newFile) {
        // First update local state for immediate feedback
        setItems((prev) => [...prev, newFile]);
        if (selectedId) {
          setCurrentFolderContents((prev) => [...prev, newFile]);
        } else {
          setCurrentFolderContents((prev) => [...prev, newFile]);
        }

        // Then refresh the entire structure in the background
        await refreshFileList();

        toast.dismiss(uploadToast);
        toast.success(
          `${isVideo ? "Video" : isAudio ? "Audio" : "File"} "${
            file.name
          }" uploaded successfully`
        );
      } else {
        toast.dismiss(uploadToast);
        toast.error("Failed to upload file: No response data");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.message || "Failed to upload file");
    } finally {
      setLoading(false);
      event.target.value = "";
    }
  };

  const handleRename = async (newName) => {
    try {
      const updatedItem = await renameItem(selectedId, newName);
      setItems((prev) =>
        prev.map((item) => (item._id === selectedId ? updatedItem : item))
      );
      toast.success("Item renamed successfully");
    } catch (error) {
      toast.error("Failed to rename item");
    }
  };

  const handleMove = async (newParentId) => {
    try {
      const updatedItem = await moveItem(selectedId, newParentId);
      setItems((prev) =>
        prev.map((item) => (item._id === selectedId ? updatedItem : item))
      );
      toast.success("Item moved successfully");
    } catch (error) {
      toast.error("Failed to move item");
    }
  };

  const handleToggle = (folderId) => {
    setExpanded((prev) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  };

  const handleSearch = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (query.trim()) {
        try {
          const results = await searchItems(query);
          setSearchResults(results);
          setShowSuggestions(true);
        } catch (error) {
          console.error("Search error:", error);
          toast.error("Failed to search items");
        }
      } else {
        setSearchResults([]);
        setShowSuggestions(false);
      }
    }, 300);
  };

  const handleSuggestionClick = (item) => {
    setSearchQuery(item.name);
    setShowSuggestions(false);
    if (item.type === "folder") {
      setSelectedId(item._id);
    }
  };

  // Filter contents based on search
  const filteredContents = currentFolderContents.filter((item) =>
    item && item.name
      ? item.name.toLowerCase().includes(searchQuery.toLowerCase())
      : false
  );

  // Handle folder selection
  const handleFolderSelect = async (folderId) => {
    try {
      setLoading(true);
      setSelectedId(folderId);

      try {
        const data = await getFolderContents(folderId);
        setCurrentFolderContents(data.contents || []);
        setCurrentFolderPath(data.path || []);
      } catch (error) {
        console.error("Error fetching folder contents:", error);
        setError("Failed to load folder contents");
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error in handleFolderSelect:", error);
      setError("Failed to select folder");
      setLoading(false);
    }
  };

  // Handle file click
  const handleFileClick = (e) => {
    // Prevent default behavior (opening the file)
    e.preventDefault();
    e.stopPropagation();
    // Do nothing for files
  };

  const handleDownload = (e, file) => {
    e.preventDefault();
    e.stopPropagation();
    // TODO: Implement download functionality
    console.log("Download file:", file);
  };

  const handleDelete = (e, file) => {
    e.preventDefault();
    e.stopPropagation();
    // TODO: Implement delete functionality
    console.log("Delete file:", file);
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div>Error: {error}</div>;

  const folderTree = buildFolderTree(items);
  const selectedItem = items.find((item) => item._id === selectedId);
  const breadcrumbs = selectedId ? getBreadcrumbs(items, selectedId) : [];

  return (
    <div style={styles.container}>
      <Toaster position="top-right" richColors />
      <div
        style={{
          display: "flex",
          height: "calc(100vh - 80px)",
          width: "100vw",
        }}
      >
        {/* Sidebar: Folder Tree */}
        <div
          style={{
            width: 280,
            background: "rgba(255,255,255,0.95)",
            borderRadius: 24,
            margin: "24px 0 24px 24px",
            padding: "20px 16px",
            boxShadow: "0 8px 32px 0 rgba(31,38,135,0.12)",
            backdropFilter: "blur(8px)",
            display: "flex",
            flexDirection: "column",
            minWidth: 220,
            border: "1px solid rgba(255,255,255,0.8)",
            marginRight: 24,
          }}
        >
          <div
            style={{
              fontSize: 15,
              color: "#64748b",
              cursor: "pointer",
              padding: "8px 12px",
              borderRadius: 10,
              marginBottom: 8,
              fontWeight: !selectedId ? 600 : 400,
              color: !selectedId ? "#2563eb" : "#64748b",
              background: !selectedId ? "rgba(37,99,235,0.08)" : "transparent",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
            onClick={() => setSelectedId(null)}
          >
            <span style={{ fontSize: 18 }}>🏠</span>
            <span>Folders</span>
          </div>
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "8px 4px",
              margin: "0 -4px",
            }}
          >
            <FolderTree
              tree={items}
              selectedId={selectedId}
              onSelect={handleFolderSelect}
              expanded={expanded}
              onToggle={(id) =>
                setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))
              }
            />
          </div>
        </div>

        {/* Main Area: Toolbar, Breadcrumbs, Grid */}
        <div
          style={{
            flex: 1,
            background: "rgba(255,255,255,0.92)",
            borderRadius: 24,
            margin: "24px 24px 24px 0",
            boxShadow: "0 8px 32px 0 rgba(31,38,135,0.10)",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {/* Toolbar */}
          <div style={styles.header}>
            <h2>My Library</h2>
            <div style={styles.actions}>
              {/* Search Bar */}
              <div style={{ position: "relative", flex: 1, maxWidth: 320 }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearch}
                  placeholder="Search folders or files..."
                  style={{
                    width: "100%",
                    padding: "0.7em 1.1em",
                    borderRadius: 10,
                    border: "1.2px solid #c7d2fe",
                    fontSize: 16,
                    boxShadow: "0 1px 4px 0 rgba(102,126,234,0.07)",
                    outline: "none",
                    background: "#f8fafc",
                  }}
                />
                {showSuggestions && searchResults.length > 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      backgroundColor: "white",
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      zIndex: 1000,
                      maxHeight: "300px",
                      overflowY: "auto",
                      marginTop: "4px",
                    }}
                  >
                    {searchResults.map((item) => (
                      <div
                        key={item._id}
                        style={{
                          padding: "8px 16px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                        onClick={() => handleSuggestionClick(item)}
                      >
                        <span style={{ fontSize: 16, marginRight: 8 }}>
                          {item.type === "folder" ? "📁" : "📄"}
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 500 }}>
                            {item.name}
                          </div>
                          <div style={{ fontSize: 12, color: "#666" }}>
                            {item.fullPath}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setShowNewFolderModal(true)}
                style={{
                  marginRight: 12,
                  fontWeight: 600,
                  borderRadius: 8,
                  fontSize: 15,
                }}
              >
                ＋ New Folder
              </button>
              <label
                className="btn btn-primary btn-sm"
                style={{
                  marginRight: 12,
                  fontWeight: 600,
                  borderRadius: 8,
                  fontSize: 15,
                }}
              >
                Upload File
                <input
                  type="file"
                  hidden
                  onChange={handleFileUpload}
                  accept={[
                    // Documents
                    ".pdf",
                    ".doc",
                    ".docx",
                    ".txt",
                    // Images
                    ".png",
                    ".jpg",
                    ".jpeg",
                    // Videos
                    ...SUPPORTED_VIDEO_FORMATS.map((ext) => `.${ext}`),
                    // Audio
                    ...SUPPORTED_AUDIO_FORMATS.map((ext) => `.${ext}`),
                  ].join(",")}
                />
              </label>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => setShowDeleteModal(true)}
                disabled={!selectedId}
                style={{
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 15,
                }}
              >
                🗑️ Delete
              </button>
            </div>
          </div>

          {/* Breadcrumbs */}
          <Breadcrumbs
            path={currentFolderPath}
            onRootClick={() => setSelectedId(null)}
            onCrumbClick={setSelectedId}
          />

          {/* Grid of Folders and Files */}
          <div
            style={{
              flex: 1,
              padding: 40,
              display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)",
              gap: 24,
              alignContent: "flex-start",
              overflowY: "auto",
            }}
          >
            {filteredContents.map((item) => (
              <FileGridItem
                key={item._id}
                item={item}
                onFolderClick={handleFolderSelect}
                onFileClick={handleFileClick}
                onDownload={handleDownload}
                onDelete={handleDelete}
                onRefresh={refreshFileList}
                getFileTypeIcon={getFileTypeIcon}
                formatFileSize={formatFileSize}
                styles={styles}
              />
            ))}
            {filteredContents.length === 0 && (
              <EmptyState
                icon="📂"
                title={
                  searchQuery
                    ? "No matching items found."
                    : "No content in this folder."
                }
              />
            )}
          </div>
        </div>
      </div>

      <ConfirmDeleteModal
        open={showDeleteModal}
        name={selectedItem?.name}
        type={selectedItem?.type}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteItem}
      />

      <InputModal
        open={showNewFolderModal}
        title="New Folder"
        label="Folder Name"
        placeholder="Enter folder name"
        value={newFolderName}
        onChange={setNewFolderName}
        onCancel={() => {
          setShowNewFolderModal(false);
          setNewFolderName("");
        }}
        onConfirm={handleCreateFolder}
      />
    </div>
  );
};

export default MyLibrary;
